import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  ITagDescriptions,
  IHeadState,
  ID_ATTRIBUTE,
} from './common';

/**
 * Stores the tags outside of React so they can be accessed during server
 * rendering after the app is finished rendering.
 *
 * Example usage:
 *
 * ```
 * const headState = new HeadState();
 *
 * const contentHtml = ReactDOMSever.renderToString(<App />);
 *
 * const headHtml = getHeadHtml(headState);
 *
 * const fullHtml = `<html><head>${headHtml}</head><body>${contentHtml}</body></html>`;
 * ```
 */
export class HeadState implements IHeadState {
  private tags: ITagDescriptions = {};

  public getTags() {
    return this.tags;
  }

  public setTags(tags: ITagDescriptions) {
    this.tags = tags;
  }
}

export function getHeadHtml(headState: IHeadState): string {
  const elements = getHeadElements(headState);

  return renderToStaticMarkup(
    <React.Fragment>
      {elements}
    </React.Fragment>,
  );
}

export function getHeadElements(headState: IHeadState): React.ReactElement[] {
  const tags = headState.getTags();

  if (!tags) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `No tags set in HeadState. Did you use <ProvideHeadState> and <SetHead>?`,
      );
    }
    return [];
  }

  const elements: React.ReactElement[] = [];

  for (const [key, description] of Object.entries(tags)) {
    if (!description) {
      continue;
    }

    const { tag, attributes } = description;

    elements.push(React.createElement(tag, {
      ...attributes,
      [ID_ATTRIBUTE]: tag === 'title' ? undefined : key,
      key,
    }));
  }

  return elements;
}
